import {
  ApiErrorTypes,
  ErrorResponse,
  ModalTypes,
  ProductGame,
  ProductType,
  PurchasabilityStatus,
  PurchaseStatus,
} from "@/@types/common";
import { ProductIdentity } from "@/@types/data/collections";
import { PurchaseState, usePurchaseInfoResponse, PurchaseData } from "@/@types/purchase";
import { startOrder } from "@/data/ordersV3";
import { useModal } from "@/providers/modal";
import { useBalance } from "@/providers/useBalance";
import { useCheckout } from "@/providers/useCheckout";
import { useCreator } from "@/providers/useCreator";
import { ActionType, useLoginActions } from "@/providers/useLoginActions";
import { useProductInfo } from "@/providers/useProductInfo";
import { AnalyticsEvent, sendAnalytics } from "@/utils/analytics";
import logger from "@/utils/logger";
import { useI18n } from "@/utils/rioti18n";
import { CURRENCY_BY_GAME, GAME_NAME_BY_GAMEID } from "@/variables";
import { navigate } from "gatsby";
import React from "react";
import { useStoredSessions } from "./useStoredSessions";
import { useXSSOAuth } from "./useXSSOAuth";
import { VirtualCurrencyCodeByProductGame } from "@/@types/currencies";

/**
 * Helper function get generate hte purchase State.
 * @param status
 * @returns
 */
const getStateFromStatus = (status: ApiErrorTypes | PurchasabilityStatus | undefined) => {
  if (status === PurchasabilityStatus.PURCHASABLE) {
    return PurchaseState.READY;
  } else if (status === ApiErrorTypes.ALREADY_OWNED) {
    return PurchaseState.ALREADY_OWNED;
  } else if (status === ApiErrorTypes.REQUIREMENTS_NOT_MET) {
    return PurchaseState.REQUIREMENTS_NOT_MET;
  } else if (status === ApiErrorTypes.NOT_AVAILABLE) {
    return PurchaseState.SERVER_ERROR;
  } else if (status === ApiErrorTypes.GAME_ACCOUNT_NOT_FOUND) {
    return PurchaseState.GAME_ACCOUNT_NOT_FOUND;
  } else if (status === ApiErrorTypes.UNSUPPORTED_REGION) {
    return PurchaseState.UNSUPPORTED_REGION;
  } else {
    // For all other cases just show a generic error.
    // Also `NOT_PURCHASEABLE` since the api doesn’t recognize the error code.
    return PurchaseState.SERVER_ERROR;
  }
};

export const usePurchaseInfo = (productIdentities: ProductIdentity[]): usePurchaseInfoResponse => {
  const { language: locale } = useI18n();
  const { startCheckout } = useCheckout();
  const { updateModal } = useModal();
  const { trafficSource, creator } = useCreator(undefined, true);
  const { isLoggedIn, login } = useXSSOAuth();
  const { saveLoginAction } = useLoginActions();
  const storedSessions = useStoredSessions();
  const [purchasing, setPurchasing] = React.useState(false);
  const [purchaseState, setPurchaseState] = React.useState(PurchaseState.LOADING);
  const affiliateName = creator?.name; // TODO: Replace affiliateName with creatorName in everywhere
  const [lastProduct, setLastProduct] = React.useState<ProductIdentity | null>();
  const defaultGameId = productIdentities[0].gameId; // TODO: Multigame support
  const { balance } = useBalance(defaultGameId);
  const totalBalance = balance?.[VirtualCurrencyCodeByProductGame[defaultGameId]] || 0;

  // Get the product details from DBUY API
  const {
    data: { productsInfo, ownedItems },
    error: productsInfoError,
    response: productsInfoResponse,
  } = useProductInfo(productIdentities);

  // Defining product to buy info
  const product = React.useMemo(
    () =>
      productsInfo?.find(
        (product) =>
          product.id === lastProduct?.productId &&
          product.game === lastProduct.gameId &&
          product.type === lastProduct.productType,
      ),
    [productsInfo, lastProduct],
  );

  const handleApiError = (errorCode: ApiErrorTypes | PurchasabilityStatus | undefined) => {
    if (errorCode === ApiErrorTypes.ALREADY_OWNED) {
      setPurchaseState(PurchaseState.ALREADY_OWNED);
    } else if (errorCode === ApiErrorTypes.REQUIREMENTS_NOT_MET) {
      setPurchaseState(PurchaseState.REQUIREMENTS_NOT_MET);
    } else if (errorCode === ApiErrorTypes.NOT_AVAILABLE) {
      setPurchaseState(PurchaseState.SERVER_ERROR);
    } else if (errorCode === ApiErrorTypes.GAME_ACCOUNT_NOT_FOUND) {
      setPurchaseState(PurchaseState.GAME_ACCOUNT_NOT_FOUND);
    } else if (errorCode === ApiErrorTypes.UNSUPPORTED_REGION) {
      updateModal(ModalTypes.UNSUPPORTED_REGION);
      setPurchaseState(PurchaseState.UNSUPPORTED_REGION);
    } else {
      // For all other cases just show a generic error.
      // Also `NOT_PURCHASEABLE` since the api doesn’t recognize the error code.
      setPurchaseState(PurchaseState.SERVER_ERROR);
    }
  };

  const initiatePurchase = async (identity: ProductIdentity, purchaseData: PurchaseData) => {
    // Show loading spinner
    setPurchasing(true);
    setLastProduct(identity);

    // Force players to log in
    if (!isLoggedIn) {
      saveLoginAction(ActionType.PURCHASE_PRODUCT, {
        autobuyProduct: `${identity.gameId}/${identity.productId}`,
        purchaseData: purchaseData,
      });
      login();
      return;
    }

    // Payment purchases will start a session and redirect
    try {
      const { status, context, error } = await startOrder(
        locale,
        purchaseData,
        trafficSource,
        storedSessions.getValues(),
      );

      // Handle the error response if its there, and show the appropriate modal.
      if (error) {
        if (error.status === 400) {
          handleApiError(error?.errorCode as ApiErrorTypes);
        } else if (error.status === 401) {
          updateModal(ModalTypes.UNAUTHORIZED);
        } else {
          handleApiError(error?.errorCode as ApiErrorTypes);
        }
        return;
      }

      // If we have a redirectUrl we'll just navigate and defer everything to payments
      if (status !== PurchaseStatus.ORDER_CONFIRMATION_REQUIRED && context?.redirect_url) {
        navigate(context.redirect_url);
        return;
      }

      // StartCheckout here will also navigate us after setting the correct state for the checkout
      await startCheckout(locale, purchaseData);
      return;
    } catch (e) {
      logger("Unable to start a purchase session", e as Error, "error");
      setPurchaseState(PurchaseState.SERVER_ERROR);
      updateModal(ModalTypes.PURCHASE_FAILED);
      sendAnalytics(AnalyticsEvent.PURCHASE_REQUEST_FAILED, {
        game_name: GAME_NAME_BY_GAMEID[identity.gameId],
        productId: identity.productId,
      });
    }

    // Request completed, disable loading spinner
    setPurchasing(false);
  };

  // Handles inital state in the BUY button
  // According to ContentStack and API data
  React.useEffect(() => {
    // Do not process anything until rso is ready otherwise we'll need to do it again
    if (!isLoggedIn) {
      return;
    }

    // Handle API data
    if (productsInfoError) {
      setPurchaseState(PurchaseState.SERVER_ERROR);
      return;
    }
    if (productsInfoResponse?.status === 200) {
      if (product && product.status !== PurchasabilityStatus.PURCHASABLE) {
        // Handle status errors which coming from the api
        handleApiError(product?.status);
      } else {
        // Porduct details are loaded
        setPurchaseState(PurchaseState.READY);
      }
      const currency = CURRENCY_BY_GAME[lastProduct?.gameId ?? defaultGameId];
      const items = productsInfo.map((product) => ({
        item_name: product?.id,
        quantity: 1,
        currency: currency,
        value: product?.cost,
      }));
      const totalValue = productsInfo.reduce((acc, product) => {
        return acc + (product?.cost ?? 0);
      }, 0);
      sendAnalytics(AnalyticsEvent.VIEW_ITEM, {
        items: items,
        currency: currency,
        value: totalValue,
      });
      return;
    } else {
      /*
          When adding new error codes ensure you also update the isError
          handler during the rendering process below ~ Line 230.
        */
      const status = productsInfoResponse?.status;
      if (status === 400) {
        const errorCode = (productsInfoResponse?.data as unknown as ErrorResponse).errorCode;
        handleApiError(errorCode as ApiErrorTypes);
        return;
      } else if (status === 401) {
        updateModal(ModalTypes.UNAUTHORIZED);
        return;
      } else if (status === 404) {
        logger("Requested product not found", new Error("Product not found"), "error", {
          gameId: lastProduct?.gameId,
          productId: lastProduct?.productId,
        });
        setPurchaseState(PurchaseState.NOT_FOUND);
      }
    }
  }, [isLoggedIn, productsInfoResponse?.status, productsInfoError, product]);

  React.useEffect(() => {
    sendAnalytics(AnalyticsEvent.BUTTON_STATE, { state: purchaseState });
  }, [purchaseState]);

  const products = productsInfo.map((product) => ({
    ...product,
    identity: {
      productId: product.id as string,
      productType: product.type as ProductType,
      gameId: product.game as ProductGame,
    },
    state: getStateFromStatus(product.status),
  }));

  return {
    products: products,
    ownedItems,
    state: purchaseState,
    affiliateName,
    balance: totalBalance,
    purchasing,
    initiatePurchase,
  };
};
